import React, { createContext, useContext, useMemo, useCallback } from 'react'
import { type PostHog } from 'posthog-js'
import {
  PostHogProvider,
  usePostHog as usePostHogOriginal,
  useFeatureFlagEnabled as useFeatureFlagEnabledOriginal,
} from 'posthog-js/react'
import LogRocket from 'logrocket'
import * as Sentry from '@sentry/react'
import { clarity } from 'react-microsoft-clarity'
import environment from '_environments'
import { UserData } from '@/types/user'
import { useUserImpersonation } from '@/utilities/Hooks/useUserImpersonation'
import { getPostHogClient } from '@/services'

export interface AnalyticsTools {
  /**
   * Whether the analytics tools are enabled. Defaults to false if the environment is local or development.
   */
  enabled: boolean
  /**
   * Enable specific analytics tools.
   */
  tools: {
    /**
     * Enable Posthog. Will enable by default in all environments, and defaults to true if key is present.
     */
    posthog: boolean
    /**
     * Enable LogRocket. Requires `enabled` to be true, and defaults to true if key is present.
     */
    logrocket: boolean
    /**
     * Enable Sentry. Requires `enabled` to be true, and defaults to true if key is present.
     */
    sentry: boolean
    /**
     * Enable Clarity. Requires `enabled` to be true, and defaults to true if key is present.
     */
    clarity: boolean
  }
}

export interface AnalyticsHook {
  /**
   * Identify a user in the analytics tools
   * @param user - The user to identify
   */
  identifyUser: (user: UserData | null) => void
  /** Identify an AuthKit session with the stable WorkOS user id. */
  identifyWorkosUser: (identity: {
    workosUserId: string | null | undefined
    localUserId: number
    name: string
    email: string
    organizationId: number | null
    organizationName: string
  }) => void
  /**
   * Reset the user in the analytics tools. Usually on logout.
   */
  resetUser: () => void
  /**
   * Track an event in the analytics tools.
   * This function is safe to call without a try/catch — it will never throw.
   * @param eventName - The name of the event to track
   * @param properties - The properties of the event to track
   */
  trackEvent: (eventName: string, properties?: Record<string, unknown>) => void
  /**
   * Whether the analytics tools are enabled and configured
   */
  enabled: AnalyticsTools['enabled']
  /**
   * An object containing the status of each tool and whether it is enabled
   */
  tools: AnalyticsTools['tools']
  /**
   * An object containing the instances of each tool
   */
  instances: {
    posthog?: ReturnType<typeof usePostHogOriginal>
    logrocket?: typeof LogRocket
    sentry?: typeof Sentry
    clarity?: typeof clarity
  }
}

interface AnalyticsProviderProps {
  children: React.ReactNode
  /**
   * Whether the analytics tools are enabled. Defaults to false if the environment is local or development.
   */
  enabled?: boolean
  /**
   * Optional configuration to override enabling specific tools
   */
  config?: Partial<AnalyticsTools>
}

const mockPosthogClient = {
  identify: () => {
    console.warn('[Analytics] PostHog is disabled. Skipping identify.')
  },
  reset: () => {
    console.warn('[Analytics] PostHog is disabled. Skipping reset.')
  },
  capture: () => {
    console.warn('[Analytics] PostHog is disabled. Skipping capture.')
  },
  isFeatureEnabled: (flag: string) => {
    console.warn(
      `[Analytics] PostHog is disabled. Returning 'false' for flag: ${flag}`
    )
    return false
  },
  onFeatureFlags: () => {}, // Add no-op for hook compatibility
  // Add any other methods you use here as no-ops when PostHog is disabled
} as unknown as PostHog

const AnalyticsContext = createContext<AnalyticsTools | undefined>(undefined)
AnalyticsContext.displayName = 'AnalyticsContext'

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  enabled,
  config,
}) => {
  const isEnabled =
    enabled ?? Boolean(!environment.isLocal && !environment.isDev)

  const analyticsConfig: AnalyticsTools = useMemo(() => {
    return {
      enabled: isEnabled,
      tools: {
        posthog:
          Boolean(environment.keys.posthog) &&
          Boolean(config?.tools?.posthog ?? true),
        logrocket:
          isEnabled &&
          Boolean(environment.keys.logrocket) &&
          Boolean(config?.tools?.logrocket ?? true),
        sentry:
          isEnabled &&
          Boolean(environment.keys.sentry) &&
          Boolean(config?.tools?.sentry ?? true),
        clarity:
          isEnabled &&
          Boolean(environment.keys.clarity) &&
          Boolean(config?.tools?.clarity ?? true),
      },
    }
  }, [
    isEnabled,
    config?.tools?.posthog,
    config?.tools?.logrocket,
    config?.tools?.sentry,
    config?.tools?.clarity,
  ])

  const posthogClient = useMemo(() => {
    if (analyticsConfig.tools.posthog && environment.keys.posthog) {
      // Get the PostHog client from services (initialized once)
      const client = getPostHogClient()
      return client ?? mockPosthogClient
    }
    return mockPosthogClient
  }, [analyticsConfig.tools.posthog])

  return (
    <AnalyticsContext.Provider value={analyticsConfig}>
      <PostHogProvider client={posthogClient}>{children}</PostHogProvider>
    </AnalyticsContext.Provider>
  )
}

export const useAnalytics = (): AnalyticsHook => {
  const context = useContext(AnalyticsContext)
  const { isImpersonating } = useUserImpersonation()

  // Use the safe PostHog hook that won't throw errors
  const posthogClient = usePostHogOriginal()

  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }

  const resetUser = useCallback(() => {
    if (context?.tools.posthog && posthogClient) {
      try {
        posthogClient.reset()
      } catch (error) {
        console.error('Failed to reset PostHog:', error)
      }
    }

    if (context.tools.sentry) {
      try {
        Sentry.setUser(null)
      } catch (error) {
        console.error('Failed to reset Sentry:', error)
      }
    }
  }, [context.tools.posthog, context.tools.sentry, posthogClient])

  const identifyAnalyticsUser = useCallback(
    (
      identity: string | null | undefined,
      properties: Record<string, string | number | null | undefined>
    ) => {
      if (!identity) {
        resetUser()
        return
      }
      const definedProperties = Object.fromEntries(
        Object.entries(properties).filter(([, value]) => value != null)
      ) as Record<string, string | number>

      if (isImpersonating) {
        console.info(
          '[Analytics] User impersonation detected. PostHog tracking is disabled to prevent data contamination.'
        )
      }

      // PostHog identification (disabled during impersonation)
      if (context.tools.posthog && posthogClient && !isImpersonating) {
        try {
          posthogClient.identify(identity, definedProperties)
        } catch (error) {
          console.error('Failed to identify user in PostHog:', error)
        }
      }

      // LogRocket identification
      if (context.tools.logrocket) {
        try {
          LogRocket.identify(identity, definedProperties)
        } catch (error) {
          console.error('Failed to identify user in LogRocket:', error)
        }
      }

      // Sentry identification
      if (context.tools.sentry) {
        try {
          Sentry.setUser({
            id: identity,
            ...definedProperties,
          })
        } catch (error) {
          console.error('Failed to identify user in Sentry:', error)
        }
      }

      // Clarity identification
      if (context.tools.clarity) {
        try {
          clarity.identify(identity, definedProperties)
          Object.entries(definedProperties).forEach(([key, value]) => {
            clarity.setTag(key, String(value))
          })
        } catch (error) {
          console.error('Failed to identify user in Clarity:', error)
        }
      }

      if (environment.isDev || environment.isLocal) {
        console.info('[Analytics] Identified user:', identity)
      }
    },
    [
      context.tools.posthog,
      context.tools.logrocket,
      context.tools.sentry,
      context.tools.clarity,
      posthogClient,
      resetUser,
      isImpersonating,
    ]
  )

  const identifyUser = useCallback(
    (user: UserData | null) => {
      const currentAccountName =
        user?.accounts?.find(account => account.id === user.accountID)?.name ??
        ''
      identifyAnalyticsUser(user?.UUID, {
        env: environment.env,
        userID: user?.UUID,
        identityProvider: 'cognito',
        name: user?.profile?.displayName ?? '',
        email: user?.profile?.emailAddress ?? '',
        accountID: user?.accountID,
        accountName: currentAccountName,
        mapID: user?.mapID,
        impersonationUrl: user?.UUID
          ? `${window.location.origin}/?userImpersonation=${user.UUID}`
          : undefined,
      })
    },
    [identifyAnalyticsUser]
  )

  const identifyWorkosUser: AnalyticsHook['identifyWorkosUser'] = useCallback(
    identity => {
      identifyAnalyticsUser(identity.workosUserId, {
        env: environment.env,
        userID: identity.workosUserId,
        localUserID: identity.localUserId,
        identityProvider: 'workos',
        name: identity.name,
        email: identity.email,
        accountID: identity.organizationId,
        accountName: identity.organizationName,
      })
    },
    [identifyAnalyticsUser]
  )

  /**
   * Track an event in the analytics tools.
   * This function is safe to call without a try/catch — it will never throw.
   * @param eventName - The name of the event to track
   * @param properties - The properties of the event to track
   */
  const trackEvent = useCallback(
    (eventName: string, properties: Record<string, unknown> = {}) => {
      try {
        // PostHog event tracking (disabled during impersonation)
        if (context.tools.posthog && posthogClient && !isImpersonating) {
          try {
            posthogClient.capture(eventName, properties)
          } catch (error) {
            console.error('Failed to capture event in PostHog:', error)
          }
        } else if (isImpersonating && environment.isDev) {
          console.info(
            `[Analytics] PostHog event "${eventName}" skipped due to user impersonation`
          )
        }

        // LogRocket event tracking
        if (context.tools.logrocket) {
          try {
            LogRocket.track(
              eventName,
              properties as Record<string, string | number | boolean>
            )
          } catch (error) {
            console.error('Failed to track event in LogRocket:', error)
          }
        }

        // Sentry breadcrumb
        if (context.tools.sentry) {
          try {
            Sentry.addBreadcrumb({
              category: 'analytics',
              message: eventName,
              data: properties,
            })
          } catch (error) {
            console.error('Failed to add breadcrumb in Sentry:', error)
          }
        }

        // Clarity event tracking
        if (context.tools.clarity) {
          try {
            clarity.setTag(eventName, JSON.stringify(properties))
          } catch (error) {
            console.error('Failed to track event in Clarity:', error)
          }
        }
      } catch (error) {
        console.error('Analytics tracking failed:', error)
      }
    },
    [
      context.tools.posthog,
      context.tools.logrocket,
      context.tools.sentry,
      context.tools.clarity,
      posthogClient,
      isImpersonating,
    ]
  )

  return {
    identifyUser,
    identifyWorkosUser,
    resetUser,
    trackEvent,
    enabled: context.enabled,
    tools: context.tools,
    instances: {
      posthog: posthogClient,
      logrocket: LogRocket,
      sentry: Sentry,
      clarity,
    },
  }
}

/**
 * Check if a PostHog feature flag is enabled.
 * @param flag - The feature flag key to check
 * @returns Whether the feature flag is enabled. Returns false if error is thrown.
 *
 * Usage:
 * ```tsx
 * const showFeature = useIsFeatureEnabled('my-feature-flag')
 * if (showFeature) {
 *   return <div>Feature is enabled</div>
 * }
 * ```
 */
export const useIsFeatureEnabled = (flag: string): boolean | undefined => {
  try {
    return useFeatureFlagEnabledOriginal(flag)
  } catch (error) {
    console.error(
      'Failed to check feature flag in PostHog. Returning false.',
      flag,
      error
    )
    return false
  }
}

export default AnalyticsProvider
